import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import { AssetRequestError } from '../api/getAssetById';
import {
  fetchFavourites,
  removeFavourite,
  type FavouriteWireRow,
} from '../api/favouritesApi';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { assetRecordMutationId, assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import { registerAssetThumbnailRecords } from '../utils/assetThumbnailStore';
import {
  clearGuestFavoriteAssets,
  favoriteAccountStorageKey,
  favoriteAssetSnapshotIsComplete,
  favoriteIdForAsset,
  favoriteIdsMatch,
  hasMigratedLocalFavorites,
  isFavoriteAssetId,
  markLocalFavoritesMigrated,
  addPendingFavoriteSyncId,
  readPendingFavoriteSyncIds,
  addPendingFavoriteRemovalId,
  readPendingFavoriteRemovalIds,
  removePendingFavoriteRemovalIds,
  prepareFavoriteAssetSnapshot,
  readFavoriteAssets,
  readGuestFavoriteAssets,
  writeFavoriteAssets,
  type FavoriteAssetEntry,
} from './favoriteAssetsStorage';
import { addFavouriteEntryToServer } from './favoriteAssetsServerPush';
import {
  combineLocalFavoriteCaches,
  favoriteEntryById,
  localFavouriteIdsMissingOnServer,
  mergeFavoritesLocalFirst,
} from './favoriteAssetsSync';

type FavoriteAssetsContextValue = {
  favorites: FavoriteAssetEntry[];
  /** True while the first server sync after login is in flight. */
  favoritesSyncing: boolean;
  isFavorite: (asset: Record<string, unknown>) => boolean;
  toggleFavorite: (asset: Record<string, unknown>) => void;
  removeFavorite: (asset: Record<string, unknown>) => void;
  /** Remove every favourite (local + server best-effort). Returns how many were removed. */
  clearAllFavorites: () => number;
};

const FavoriteAssetsContext = createContext<FavoriteAssetsContextValue | null>(null);

const FAVORITES_FETCH_RETRY_ATTEMPTS = 3;
const FAVORITES_FETCH_RETRY_DELAY_MS = 400;

async function fetchFavouritesWithRetry(): Promise<FavouriteWireRow[]> {
  let lastError: unknown;
  for (let attempt = 0; attempt < FAVORITES_FETCH_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await fetchFavourites();
    } catch (err) {
      lastError = err;
      if (attempt < FAVORITES_FETCH_RETRY_ATTEMPTS - 1) {
        await new Promise((resolve) => {
          setTimeout(resolve, FAVORITES_FETCH_RETRY_DELAY_MS * (attempt + 1));
        });
      }
    }
  }
  throw lastError;
}

function readInitialFavoriteEntries(accountKey: string | null): FavoriteAssetEntry[] {
  const combined = accountKey
    ? readFavoriteAssets(accountKey)
    : combineLocalFavoriteCaches(readFavoriteAssets(null), readGuestFavoriteAssets());
  const pendingRemovals = readPendingFavoriteRemovalIds();
  if (pendingRemovals.length === 0) {
    return combined;
  }
  return combined.filter(
    (entry) => !pendingRemovals.some((removedId) => favoriteIdsMatch(removedId, entry.id)),
  );
}

function favoriteRemovalIdCandidates(entry: FavoriteAssetEntry): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      const trimmed = raw.trim();
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(trimmed);
      }
    } else if (typeof raw === 'number' && Number.isFinite(raw)) {
      const trimmed = String(raw);
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(trimmed);
      }
    }
  };
  add(entry.id);
  const asset = coalesceAssetListRecord(entry.asset);
  add(assetRecordMutationId(asset));
  add(assetRecordPrimaryId(asset));
  for (const key of ['asset_id', 'assetId', 'AssetId', 'id', 'Id', 'UUID', 'uuid'] as const) {
    add(asset[key]);
  }
  return out;
}

async function deleteFavouriteFromServer(entry: FavoriteAssetEntry): Promise<boolean> {
  const candidates = favoriteRemovalIdCandidates(entry);
  const tried = new Set<string>();
  for (const candidate of candidates) {
    const key = candidate.toLowerCase();
    if (tried.has(key)) {
      continue;
    }
    tried.add(key);
    try {
      await removeFavourite(candidate);
      removePendingFavoriteRemovalIds(candidates);
      return true;
    } catch (err) {
      if (err instanceof AssetRequestError && err.httpStatus === 404) {
        removePendingFavoriteRemovalIds(candidates);
        return true;
      }
      if (!(err instanceof AssetRequestError) || err.httpStatus !== 400) {
        throw err;
      }
    }
  }
  return false;
}

export function FavoriteAssetsProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, isAuthReady } = useAuth();
  const accountKeyRef = useRef<string | null>(favoriteAccountStorageKey());
  const signedInAccountKey = isLoggedIn ? favoriteAccountStorageKey() : null;
  const [favorites, setFavorites] = useState<FavoriteAssetEntry[]>(() =>
    readInitialFavoriteEntries(accountKeyRef.current),
  );
  const [favoritesSyncing, setFavoritesSyncing] = useState(false);
  const favoritesRef = useRef(favorites);
  const syncGenRef = useRef(0);
  const pendingApiIdsRef = useRef(new Set<string>());

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  const persist = useCallback((next: FavoriteAssetEntry[], accountKey = favoriteAccountStorageKey()) => {
    accountKeyRef.current = accountKey;
    favoritesRef.current = next;
    setFavorites(next);
    writeFavoriteAssets(next, accountKey);
    registerAssetThumbnailRecords(next.map((entry) => entry.asset));
  }, []);

  const syncFromServer = useCallback(async () => {
    const accountKey = favoriteAccountStorageKey();
    const previousAccount = accountKeyRef.current;
    accountKeyRef.current = accountKey;
    const gen = ++syncGenRef.current;
    setFavoritesSyncing(true);
    try {
      const switchedAccount = Boolean(previousAccount && accountKey && previousAccount !== accountKey);
      const userCache = readFavoriteAssets(accountKey);
      const guestCache = accountKey ? [] : readGuestFavoriteAssets();
      const pendingRemovals = readPendingFavoriteRemovalIds();
      const inMemoryCache = switchedAccount ? [] : favoritesRef.current;
      const localCache = accountKey
        ? combineLocalFavoriteCaches(userCache, inMemoryCache)
        : combineLocalFavoriteCaches(userCache, guestCache, inMemoryCache);

      if (switchedAccount) {
        persist(userCache, accountKey);
      }

      if (pendingRemovals.length > 0) {
        const removalEntries = pendingRemovals.map((id) => ({
          id,
          asset: prepareFavoriteAssetSnapshot({ asset_id: id, id }),
          savedAt: 0,
        }));
        await Promise.all(
          removalEntries.map(async (entry) => {
            try {
              await deleteFavouriteFromServer(entry);
            } catch {
              /* retry next sync */
            }
          }),
        );
      }

      let serverRows: FavouriteWireRow[] = await fetchFavouritesWithRetry();

      const shouldMigrate = Boolean(accountKey) && !hasMigratedLocalFavorites(accountKey!);
      const activePendingRemovals = readPendingFavoriteRemovalIds();
      const toMigrate = localFavouriteIdsMissingOnServer(localCache, serverRows).filter(
        (id) => {
          if (activePendingRemovals.some((removedId) => favoriteIdsMatch(removedId, id))) {
            return false;
          }
          const localEntry = favoriteEntryById(localCache, id);
          return Boolean(localEntry && favoriteAssetSnapshotIsComplete(localEntry.asset));
        },
      );
      if (toMigrate.length > 0) {
        await Promise.all(
          toMigrate.map(async (id) => {
            const localEntry = favoriteEntryById(localCache, id);
            try {
              if (localEntry) {
                const ok = await addFavouriteEntryToServer(localEntry);
                if (!ok) {
                  addPendingFavoriteSyncId(id);
                }
              } else {
                const ok = await addFavouriteEntryToServer({
                  id,
                  asset: prepareFavoriteAssetSnapshot({ asset_id: id, id }),
                  savedAt: Date.now(),
                });
                if (!ok) {
                  addPendingFavoriteSyncId(id);
                }
              }
            } catch {
              addPendingFavoriteSyncId(id);
            }
          }),
        );
        try {
          serverRows = await fetchFavouritesWithRetry();
        } catch {
          serverRows = [
            ...serverRows,
            ...toMigrate.map((assetId) => ({
              assetId,
              savedAt: Date.now(),
              asset: null as Record<string, unknown> | null,
            })),
          ];
        }
      }

      const stillMissing = localFavouriteIdsMissingOnServer(localCache, serverRows);
      if (accountKey && shouldMigrate && stillMissing.length === 0) {
        markLocalFavoritesMigrated(accountKey);
        clearGuestFavoriteAssets();
      }

      if (gen !== syncGenRef.current) {
        return;
      }

      const merged = await mergeFavoritesLocalFirst(
        localCache,
        serverRows,
        readPendingFavoriteRemovalIds(),
      );
      if (gen !== syncGenRef.current) {
        return;
      }
      persist(merged, accountKey);
    } catch {
      const fallbackSource = accountKey
        ? combineLocalFavoriteCaches(readFavoriteAssets(accountKey), favoritesRef.current)
        : combineLocalFavoriteCaches(
            readFavoriteAssets(accountKey),
            readGuestFavoriteAssets(),
            favoritesRef.current,
          );
      const fallback = fallbackSource.filter(
        (entry) =>
          !readPendingFavoriteRemovalIds().some((removedId) =>
            favoriteIdsMatch(removedId, entry.id),
          ),
      );
      if (gen === syncGenRef.current) {
        persist(fallback, accountKey);
      }
    } finally {
      if (gen === syncGenRef.current) {
        setFavoritesSyncing(false);
      }
    }
  }, [persist]);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }
    if (!isLoggedIn) {
      syncGenRef.current += 1;
      setFavoritesSyncing(false);
      accountKeyRef.current = null;
      const local = readGuestFavoriteAssets();
      setFavorites(local);
      favoritesRef.current = local;
      return;
    }
    void syncFromServer();
  }, [isAuthReady, isLoggedIn, signedInAccountKey, syncFromServer]);

  const isFavorite = useCallback(
    (asset: Record<string, unknown>) => {
      const id = favoriteIdForAsset(asset);
      return id ? isFavoriteAssetId(id, favorites) : false;
    },
    [favorites],
  );

  const toggleFavorite = useCallback(
    (asset: Record<string, unknown>) => {
      const id = favoriteIdForAsset(asset);
      if (!id) {
        return;
      }
      const current = favoritesRef.current;
      const existing = favoriteEntryById(current, id);
      if (existing) {
        const next = current.filter((entry) => !favoriteIdsMatch(entry.id, id));
        addPendingFavoriteRemovalId(existing.id);
        persist(next);
        if (isLoggedIn) {
          if (pendingApiIdsRef.current.has(`del:${id}`)) {
            return;
          }
          pendingApiIdsRef.current.add(`del:${id}`);
          void deleteFavouriteFromServer(existing).finally(() => {
            pendingApiIdsRef.current.delete(`del:${id}`);
          });
        }
        return;
      }
      const entry: FavoriteAssetEntry = {
        id,
        asset: prepareFavoriteAssetSnapshot(asset),
        savedAt: Date.now(),
      };
      removePendingFavoriteRemovalIds(favoriteRemovalIdCandidates(entry));
      persist([entry, ...current.filter((e) => !favoriteIdsMatch(e.id, id))]);
      if (isLoggedIn) {
        if (pendingApiIdsRef.current.has(`add:${id}`)) {
          return;
        }
        pendingApiIdsRef.current.add(`add:${id}`);
        void addFavouriteEntryToServer(entry)
          .then((synced) => {
            if (!synced) {
              addPendingFavoriteSyncId(id);
            }
          })
          .catch(() => {
            addPendingFavoriteSyncId(id);
          })
          .finally(() => {
            pendingApiIdsRef.current.delete(`add:${id}`);
          });
      }
    },
    [isLoggedIn, persist],
  );

  const removeFavorite = useCallback(
    (asset: Record<string, unknown>) => {
      const id = favoriteIdForAsset(asset);
      if (!id) {
        return;
      }
      const current = favoritesRef.current;
      const existing = favoriteEntryById(current, id);
      if (!existing) {
        return;
      }
      addPendingFavoriteRemovalId(existing.id);
      persist(current.filter((entry) => !favoriteIdsMatch(entry.id, id)));
      if (isLoggedIn) {
        if (pendingApiIdsRef.current.has(`del:${id}`)) {
          return;
        }
        pendingApiIdsRef.current.add(`del:${id}`);
        void deleteFavouriteFromServer(existing).finally(() => {
          pendingApiIdsRef.current.delete(`del:${id}`);
        });
      }
    },
    [isLoggedIn, persist],
  );

  const clearAllFavorites = useCallback((): number => {
    const current = favoritesRef.current;
    if (current.length === 0) {
      return 0;
    }
    const removed = current.length;
    for (const entry of current) {
      addPendingFavoriteRemovalId(entry.id);
    }
    persist([]);
    if (isLoggedIn) {
      for (const entry of current) {
        const id = entry.id;
        if (pendingApiIdsRef.current.has(`del:${id}`)) {
          continue;
        }
        pendingApiIdsRef.current.add(`del:${id}`);
        void deleteFavouriteFromServer(entry).finally(() => {
          pendingApiIdsRef.current.delete(`del:${id}`);
        });
      }
    }
    return removed;
  }, [isLoggedIn, persist]);

  const value = useMemo(
    () => ({
      favorites,
      favoritesSyncing,
      isFavorite,
      toggleFavorite,
      removeFavorite,
      clearAllFavorites,
    }),
    [favorites, favoritesSyncing, isFavorite, toggleFavorite, removeFavorite, clearAllFavorites],
  );

  return <FavoriteAssetsContext.Provider value={value}>{children}</FavoriteAssetsContext.Provider>;
}

export function useFavoriteAssets(): FavoriteAssetsContextValue {
  const ctx = useContext(FavoriteAssetsContext);
  if (!ctx) {
    throw new Error('useFavoriteAssets must be used within FavoriteAssetsProvider');
  }
  return ctx;
}
